import { useState } from "react";
import {
  Box,
  Button as MuiButton,
  Chip,
  Collapse,
  Divider,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { BaseDialog } from "../../modals/BaseDialog";
import { organizationPrimaryColors } from "../../../layouts/PermanentSidebarMUI";
import Button from "../../Button";

const ProductLocationsDropdown = ({ productItem }) => {
  const [expanded, setExpanded] = useState(false);
  const locations = productItem._locations ?? [];

  if (locations.length < 2) return null;

  const panelId = `product-locations-${productItem.id}`;

  return (
    <Box mt={0.5}>
      <MuiButton
        size="small"
        onClick={() => setExpanded((current) => !current)}
        aria-expanded={expanded}
        aria-controls={panelId}
        endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{ px: 0, minHeight: 30, textTransform: "none" }}>
        Ver estoque por localização ({locations.length})
      </MuiButton>
      <Collapse in={expanded} unmountOnExit>
        <Box
          id={panelId}
          role="region"
          aria-label={`Estoque por localização do item ${productItem.code}`}
          sx={{ mt: 0.5, px: 1.25, borderRadius: 1, bgcolor: "action.hover" }}>
          {locations.map((location, index) => (
            <Box key={location.id ?? `${location.label}-${index}`}>
              {index > 0 && <Divider />}
              <Box
                sx={{
                  minHeight: 34,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  py: 0.5,
                }}>
                <Typography variant="body2" color="text.secondary">
                  {location.label}
                </Typography>
                <Typography variant="body2" fontWeight={600} whiteSpace="nowrap">
                  {Number(location.quantity || 0).toLocaleString("pt-BR")} un.
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};

export const ModalSearchProduct = ({
  openProductSearchModal,
  handleCloseProductSearchModal,

  productFilter,
  setProductFilter,

  productSearchItem,

  findAssetData,
  assetsData,

  setProducts,
  setValue,
  debouncedAddProduct,
}) => {
  return (
    <BaseDialog
      open={openProductSearchModal}
      onClose={handleCloseProductSearchModal}
      title="Insumos disponíveis"
      maxWidth="md">
      <Box p={2} display="flex" flexDirection="column" gap={2}>
        {/* CAMPO DE BUSCA */}
        <TextField
          size="small"
          label="Buscar"
          placeholder="Buscar por nome, código, categoria, endereço..."
          value={productFilter}
          onChange={(e) => setProductFilter(e.target.value)}
          fullWidth
        />

        {/* PRODUTOS FILTRADOS */}
        {productSearchItem?.product_items?.length > 0 &&
          productSearchItem.product_items
            .filter((productItem) => {
              if (!productFilter) return true;

              const search = productFilter.toLowerCase();

              const fieldsToSearch = [
                productSearchItem?.alias,
                productItem?.code,
                productItem?.category?.name,
                productItem?.condition?.name,
                productItem?.address?.formatted_address,
                productItem?.location?.formatted_location,
                productItem?._locationsText,
                productItem?.address?.addressable?.alias,
              ];

              return fieldsToSearch.some(
                (field) => field && field.toLowerCase().includes(search),
              );
            })
            .map((productItem) => {
              // LÓGICA DE COLORAÇÃO DO BACKGROUND DO PRODUTO
              const requestedQuantity =
                productSearchItem?.requested_quantity || 0;
              const availableQuantity = productItem.quantity || 0;

              let backgroundColor = "#fdfdfdff";

              if (availableQuantity < requestedQuantity) {
                backgroundColor = "#fcf8e7ff"; // amarelo
              }

              if (availableQuantity === 0) {
                backgroundColor = "#ffe7e7ff"; // vermelho claro
              }

              return (
                <Paper
                  key={productItem.id}
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 3,
                    alignItems: "center",
                    backgroundColor: backgroundColor,
                    ":hover": { backgroundColor: "#f0f0f0ff" },
                  }}>
                  {/* INFORMAÇÕES DO PRODUTO */}
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight="bold">
                      {`${productSearchItem.alias} | ${productItem?.category?.name} | ${productItem?.condition?.name}`}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Organização:{" "}
                      <Chip
                        label={productItem.address?.addressable?.alias}
                        size="small"
                        color="primary"
                        sx={{
                          backgroundColor: `${
                            organizationPrimaryColors[
                              productItem.address?.addressable?.name
                            ]
                          }`,
                          fontSize: "11px",
                        }}
                      />{" "}
                      — Código: {productItem.code}
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Endereço: {productItem.address?.formatted_address}
                    </Typography>

                    {productItem.location?.formatted_location && (
                      <Typography variant="body2" color="text.secondary">
                        Localização: {productItem.location?.formatted_location}
                      </Typography>
                    )}
                    <ProductLocationsDropdown productItem={productItem} />
                  </Box>

                  {/* QUANTIDADE EM ESTOQUE */}
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Estoque
                    </Typography>

                    <Typography fontWeight="bold" textAlign="center">
                      {productItem.quantity}
                    </Typography>
                  </Box>

                  {/* BOTAO DE SELECIONAR PRODUTO */}
                  <Button
                    text="Selecionar"
                    size="small"
                    variant="contained"
                    disabled={availableQuantity < 1}
                    onClick={() => {
                      // Se o usuário trocar de insumo que já foi colado em volume, remover o insumo antigo do volume
                      const currentAsset = findAssetData(
                        assetsData,
                        productSearchItem,
                      );

                      if (currentAsset && currentAsset.id !== productItem.id) {
                        let totalRemoved = 0;

                        // Repor a Q. Res
                        if (totalRemoved > 0) {
                          setProducts((prev) =>
                            prev.map((p) => {
                              if (
                                p.product_id === productSearchItem.product_id
                              ) {
                                return {
                                  ...p,
                                  quantity: p.quantity + totalRemoved,
                                };
                              }

                              return p;
                            }),
                          );
                        }
                      }

                      // Setar o código no input
                      setValue(
                        `equipmentsNumber.${productSearchItem.identify}`,
                        productItem.code,
                        { shouldDirty: true },
                      );

                      debouncedAddProduct(
                        productItem.code,
                        productSearchItem.identify,
                        "product_id",
                        productSearchItem.product_id,
                      );

                      handleCloseProductSearchModal();
                    }}
                  />
                </Paper>
              );
            })}

        {/* CASO NÃO TENHA RESULTADOS */}
        {(!productSearchItem?.product_items ||
          productSearchItem.product_items.filter((productItem) => {
            if (!productFilter) return true;

            const search = productFilter.toLowerCase();

            const fieldsToSearch = [
              productSearchItem?.alias,
              productItem?.code,
              productItem?.category?.name,
              productItem?.condition?.name,
              productItem?.address?.formatted_address,
              productItem?.location?.formatted_location,
              productItem?._locationsText,
              productItem?.address?.addressable?.alias,
            ];

            return fieldsToSearch.some(
              (field) => field && field.toLowerCase().includes(search),
            );
          }).length === 0) && (
          <Typography color="text.secondary" textAlign="center">
            Nenhum insumo encontrado
          </Typography>
        )}
      </Box>
    </BaseDialog>
  );
};
